import {useState} from 'react';
import {Link} from 'react-router-dom';
import {Grid, Container, Header, Image, Message, List} from 'semantic-ui-react';

import httpClient from 'httpClient';
import gitlabIcon from 'static/gitlab_icon.png';

import GitlabPagesSiteCreationForm from './GitlabPagesSiteCreationForm';

import styles from './GitlabPagesSiteCreation.module.scss';

const GitlabPagesSiteCreation = () => {
  const [showForm, setShowForm] = useState(true);
  const [createdSiteInfo, setCreatedSiteInfo] = useState({name: null, url: null});
  const [creationError, setCreationError] = useState(null);

  const createGitlabPagesSite = async newSiteData => {
    setCreationError(null);
    try {
      await httpClient.post('/api/gps/sites', newSiteData, {handleErrorsCentrally: false});
      setCreatedSiteInfo({
        name: newSiteData.name,
        url: new URL(`https://${newSiteData.name}${newSiteData.subdomain}`),
      });
      setShowForm(false);
    } catch (err) {
      let creationErrorMessage = '';
      if (err.response.data.errors) {
        creationErrorMessage += ' ' + JSON.stringify(err.response.data.errors.json);
      }
      if (err.response.data.error) {
        creationErrorMessage += ' ' + err.response.data.error;
      }
      setCreationError(creationErrorMessage);
    }
  };

  return (
    <Grid centered>
      <Grid.Column className={styles.GitlabPagesSiteCreation}>
        <Container>
          <Header as="h3" className={styles.Header}>
            <Image src={gitlabIcon} alt="gitlab icon" className={styles.HeaderIcon} /> Creating a
            GitLab Pages site
          </Header>
          {showForm ? (
            <div>
              <p>You're minutes away from getting your GitLab Pages site up and running.</p>
              <List>
                <List.Item>
                  1. GitLab Pages sites have their content stored in a GitLab repository.{' '}
                  <a
                    href="https://how-to.docs.cern.ch/gitlabpagessite/create_site/create_gitlab_repo/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    See how to create it.
                  </a>
                </List.Item>
                <List.Item>2. Fill in the form below to create a URL for your site.</List.Item>
                <List.Item>
                  3. Add this new URL to your GitLab repository.{' '}
                  <a
                    href="https://how-to.docs.cern.ch/gitlabpagessite/create_site/set_up_pages/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    See how.
                  </a>
                </List.Item>
              </List>
            </div>
          ) : (
            <div>
              <p>
                The GitLab Pages site has been created. Please add the site's URL:{' '}
                <a href={createdSiteInfo.url}>{createdSiteInfo.url.toString()}</a> to your GitLab
                repository, as explained{' '}
                <a
                  href="https://how-to.docs.cern.ch/gitlabpagessite/create_site/set_up_pages/"
                  target="_blank"
                  rel="noreferrer"
                >
                  here
                </a>
                .
              </p>
              <p>
                It may take a few minutes until the site becomes available due to the DNS
                propagation delay.
              </p>
              <p>
                You can also further{' '}
                <Link to={`gps/${createdSiteInfo.name}`}>configure your site</Link>.
              </p>
            </div>
          )}
        </Container>
        {creationError && (
          <Message negative>
            <Message.Header>There was a problem creating the GitLab Pages site</Message.Header>
            <p>{creationError}</p>
            <p>
              Please try again or open a{' '}
              <a
                href="https://cern.service-now.com/service-portal?id=service_element&name=web-portal"
                target="_blank"
                rel="noreferrer"
              >
                ticket
              </a>
            </p>
          </Message>
        )}
        {showForm && <GitlabPagesSiteCreationForm create={createGitlabPagesSite} />}
      </Grid.Column>
    </Grid>
  );
};

export default GitlabPagesSiteCreation;
